<template>
  <!--
    A11Y-REF (WCAG/WAI)
    - WCAG 2.1.1 Keyboard: native button supports Enter/Space activation by default.
    - WCAG 4.1.2 Name, Role, Value: role is native button; name comes from ariaLabel or inner text.
    - WCAG 2.5.8 Target Size (Minimum, WCAG 2.2): consuming pages should ensure adequate hit area.
    - WAI-ARIA APG Button Pattern: use native <button> instead of div[role="button"].
  -->
  <component
    :is="resolvedTag"
    class="card-list-item__control def-card-list-item"
    v-bind="controlAttrs"
    @click="handleClick"
    @keydown="handleKeydown"
    @keyup="handleKeyup"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: "CardListItem",
  inheritAttrs: false,
  props: {
    // A11Y-REF WCAG 4.1.2: explicit accessible name override for icon/summary-style cards.
    ariaLabel: {
      type: String,
      default: "",
    },
    ariaLabelledby: {
      type: String,
      default: "",
    },
    ariaDescribedby: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    interactive: {
      default: undefined,
      validator(value) {
        return value === undefined || value === null || typeof value === "boolean";
      },
    },
    tag: {
      type: String,
      default: "div",
    },
    interactiveTag: {
      type: String,
      default: "button",
    },
    type: {
      type: String,
      default: "button",
    },
  },
  data() {
    return {
      pressedWithSpace: false,
    };
  },
  computed: {
    isInteractive() {
      if (typeof this.interactive === "boolean") {
        return this.interactive;
      }
      return !!(this.$listeners && this.$listeners.click);
    },
    resolvedTag() {
      return this.isInteractive ? this.interactiveTag : this.tag;
    },
    isNativeButton() {
      return this.resolvedTag === "button";
    },
    controlAttrs() {
      const attrs = {
        ...this.$attrs,
        "aria-label": this.ariaLabel || null,
        "aria-labelledby": this.ariaLabelledby || null,
        "aria-describedby": this.ariaDescribedby || null,
      };

      if (this.isNativeButton) {
        attrs.type = this.type || "button";
        attrs.disabled = this.disabled;
        return attrs;
      }

      delete attrs.type;

      if (this.isInteractive) {
        attrs.role = attrs.role || "button";
        attrs.tabindex = this.disabled ? -1 : attrs.tabindex != null ? attrs.tabindex : 0;
        attrs["aria-disabled"] = this.disabled ? "true" : null;
      } else if (this.disabled) {
        attrs["aria-disabled"] = "true";
      }

      return attrs;
    },
  },
  methods: {
    shouldIgnoreNestedInteractive(event) {
      if (!event || !event.target || event.target === event.currentTarget) {
        return false;
      }
      if (typeof event.target.closest !== "function") {
        return false;
      }

      const nestedInteractive = event.target.closest(
        'a, button, input, select, textarea, summary, [role="button"], [role="link"], [role="checkbox"], [role="menuitem"], [role="option"], [role="radio"], [role="switch"], [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
      );

      return !!nestedInteractive && nestedInteractive !== event.currentTarget;
    },
    emitClick(event) {
      this.$emit("click", event);
    },
    handleClick(event) {
      if (!this.isInteractive || this.disabled || this.shouldIgnoreNestedInteractive(event)) {
        if (this.disabled && event) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      this.emitClick(event);
    },
    handleKeydown(event) {
      if (!this.isInteractive || this.isNativeButton || this.disabled) {
        return;
      }

      if (event.repeat) {
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        this.emitClick(event);
      } else if (event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        this.pressedWithSpace = true;
      }
    },
    handleKeyup(event) {
      if (!this.isInteractive || this.isNativeButton || this.disabled) {
        return;
      }

      if ((event.key === " " || event.key === "Spacebar") && this.pressedWithSpace) {
        this.pressedWithSpace = false;
        event.preventDefault();
        this.emitClick(event);
      }
    },
  },
};
</script>

